import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentToken } from "../features/slices/authSlice";
import { addPushNotification } from "../features/slices/notificationSlice";
import { apiSlice } from "../features/api/apiSlice";

const WebSocketContext = createContext({
  isConnected: false,
  lastMessage: null,
  sendMessage: () => {},
});

const MAX_RECONNECT_DELAY = 30000;
const INITIAL_RECONNECT_DELAY = 1000;

export const WebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  const wsUrl = process.env.REACT_APP_WS_URL;

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null; // Prevent reconnection on intentional close
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!wsUrl || !token) return;

    // Don't connect if already connected or connecting
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      const url = `${wsUrl}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log("[WS] Connected to", wsUrl);
        setIsConnected(true);
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);

          if (data.type === "NOTIFICATION" && data.data && data.data.id) {
            const apiUrl = process.env.REACT_APP_API_URL;
            fetch(`${apiUrl}/notification/api/v1/${data.data.id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((res) => res.json())
              .then((res) => {
                if (res?.success && res?.result) {
                  dispatch(addPushNotification(data.data));
                }
              })
              .catch(() => {});
            dispatch(apiSlice.util.invalidateTags(["NotificationCount"]));
          }
        } catch (err) {
          console.error("[WS] Parse error:", err);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;

        // Only reconnect if we still have a token (not a logout)
        if (token && !event.wasClean) {
          const delay = Math.min(reconnectDelayRef.current, MAX_RECONNECT_DELAY);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY);
            connect();
          }, delay);
        }
      };

      ws.onerror = () => {
        // onerror is always followed by onclose, so reconnection is handled there
      };

      wsRef.current = ws;
    } catch (err) {
      // WebSocket constructor can throw if URL is invalid
      console.error("WebSocket connection error:", err);
    }
  }, [wsUrl, token, dispatch]);

  // Connect when token is available, disconnect on logout
  useEffect(() => {
    if (token && wsUrl) {
      connect();
    } else {
      cleanup();
    }

    return cleanup;
  }, [token, wsUrl, connect, cleanup]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        typeof message === "string" ? message : JSON.stringify(message)
      );
    }
  }, []);

  return (
    <WebSocketContext.Provider value={{ isConnected, lastMessage, sendMessage }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);

export default WebSocketContext;
